import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import '../styles/dashboard.css'

const ISSUE_AREA_COLORS = {
  Collection: { bg: '#fef3c7', color: '#92400e' },
  Product: { bg: '#dbeafe', color: '#1e40af' },
  Deal: { bg: '#dcfce7', color: '#166534' },
  Order: { bg: '#f3e8ff', color: '#7c3aed' },
  Payment: { bg: '#fce7f3', color: '#be185d' },
  Other: { bg: '#f3f4f6', color: '#4b5563' }
}

// KPI Card Component
const KPI = ({ title, value, sub, tone, icon, to, subAlert }) => (
  <Link to={to} className={`kpi kpi--${tone}`}>
    <div className="kpi__top">
      <div className="kpi__title">{title}</div>
      <div className="kpi__icon" aria-hidden="true">{icon}</div>
    </div>
    <div className="kpi__value">{value}</div>
    <div className={`kpi__sub ${subAlert ? 'kpi__sub--alert' : ''}`}>{sub}</div>
  </Link>
)

// Task Row Component
const TaskRow = ({ label, badge, active, icon, to, badgeAlert }) => (
  <Link to={to} className={`taskRow ${active ? 'taskRow--active' : ''}`}>
    <span className="taskRow__left">
      <span className="taskRow__icon" aria-hidden="true">{icon}</span>
      <span className="taskRow__label">{label}</span>
      {badge ? (
        <span className={`pill ${active ? 'pill--onDark' : ''} ${badgeAlert ? 'pill--alert' : ''}`}>
          {badge}
        </span>
      ) : null}
    </span>
    <span className="taskRow__chev" aria-hidden="true">&rsaquo;</span>
  </Link>
)

// Feed Item Component
const FeedItem = ({ time, title, body, icon, tone }) => (
  <div className="feedItem">
    <div className="feedItem__time">{time}</div>
    <div className="feedItem__row">
      <div className={`feedItem__icon ${tone ? `feedItem__icon--${tone}` : ''}`} aria-hidden="true">
        {icon}
      </div>
      <div className="feedItem__text">
        <div className="feedItem__title">{title}</div>
        <div className="feedItem__body">{body}</div>
      </div>
    </div>
  </div>
)

function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    quoteRequested: 0,
    awaitingLabel: 0,
    arrivedToTest: 0,
    awaitingPayment: 0,
    unfulfilled: 0,
    openTickets: 0
  })
  const [activities, setActivities] = useState([])
  const [activitiesLoading, setActivitiesLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [dueTodayItems, setDueTodayItems] = useState([])
  const [dueTodayLoading, setDueTodayLoading] = useState(true)

  useEffect(() => {
    loadDashboardStats()
    loadActivities()
    loadDueTodayItems()
  }, [])

  async function loadDueTodayItems() {
    setDueTodayLoading(true)
    try {
      const response = await api.get('/api/pinned/due-today')
      setDueTodayItems(response.items || [])
    } catch (error) {
      console.error('Failed to load due today items:', error)
    } finally {
      setDueTodayLoading(false)
    }
  }

  function navigateToPinnedItem(item) {
    // Clean entity_id (remove .0 if present from numeric storage)
    const entityId = String(item.entity_id).replace('.0', '')

    if (item.entity_type === 'deal') {
      navigate(`/deals/all?dealId=${entityId}`)
    } else if (item.entity_type === 'order') {
      navigate(`/orders?orderId=${entityId}`)
    } else if (item.entity_type === 'product') {
      navigate(`/inventory/list?productId=${entityId}`)
    } else if (item.entity_type === 'ticket') {
      navigate(`/tickets?ticketId=${entityId}`)
    }
  }

  async function markAsDealtWith(id, e) {
    e.stopPropagation()
    try {
      await api.patch(`/api/pinned/${id}/complete`)
      setDueTodayItems(prev => prev.filter(item => item.id !== id))
    } catch (error) {
      console.error('Failed to complete pin:', error)
    }
  }

  async function loadActivities() {
    setActivitiesLoading(true)
    try {
      const response = await api.get('/api/activities?limit=10')
      setActivities(response.activities || [])
    } catch (error) {
      console.error('Failed to load activities:', error)
    } finally {
      setActivitiesLoading(false)
    }
  }

  function formatActivityTime(timestamp) {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  }

  async function loadDashboardStats() {
    setLoading(true)
    try {
      const [dealsStats, ordersStats, ticketsStats] = await Promise.all([
        api.get('/api/deals/dashboard-stats').catch(() => ({ stats: {} })),
        api.get('/api/orders/shopify/unfulfilled-count').catch(() => ({ count: 0 })),
        api.get('/api/tickets/counts').catch(() => ({ counts: { open: 0 } }))
      ])

      setStats({
        quoteRequested: dealsStats.stats?.quoteRequested || 0,
        awaitingLabel: dealsStats.stats?.awaitingLabel || 0,
        arrivedToTest: dealsStats.stats?.arrivedToTest || 0,
        awaitingPayment: dealsStats.stats?.awaitingPayment || 0,
        unfulfilled: ordersStats.count || 0,
        openTickets: ticketsStats.counts?.open || 0
      })
    } catch (error) {
      console.error('Failed to load dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  function getGreeting() {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const totalPending = stats.quoteRequested + stats.awaitingLabel + stats.arrivedToTest + stats.awaitingPayment

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="pageHeader">
        <h1 className="pageTitle">
          {getGreeting()}, <span className="pageTitle__name">{user?.name || 'admin'}</span>{' '}
          <span aria-hidden="true">👋</span>
        </h1>
        <p className="pageSub">Here's a quick update on current operations.</p>
      </header>

      {/* KPI Cards */}
      <section className="kpiGrid">
        <KPI
          title="Quote Requests"
          value={loading ? '-' : stats.quoteRequested}
          sub={stats.quoteRequested > 0 ? `${stats.quoteRequested} awaiting review` : 'All clear'}
          tone="mint"
          icon="🔎"
          to="/deals/quote-requested"
        />
        <KPI
          title="Items in Testing"
          value={loading ? '-' : stats.arrivedToTest}
          sub={stats.arrivedToTest > 0 ? 'View testing queue' : 'None in testing'}
          tone="sand"
          icon="⚗️"
          to="/warehouse/testing"
        />
        <KPI
          title="Collections to Book"
          value={loading ? '-' : stats.awaitingLabel}
          sub={stats.awaitingLabel > 0 ? 'Awaiting labels' : 'All booked'}
          tone="peach"
          icon="📦"
          to="/deals/collections"
        />
        <KPI
          title="Orders to Fulfil"
          value={loading ? '-' : stats.unfulfilled}
          sub={stats.unfulfilled > 0 ? `${stats.unfulfilled} pending dispatch` : 'All fulfilled'}
          subAlert={stats.unfulfilled > 0}
          tone="rose"
          icon="🚚"
          to="/orders?status=unfulfilled"
        />
        <KPI
          title="Deals to Pay"
          value={loading ? '-' : stats.awaitingPayment}
          sub={stats.awaitingPayment > 0 ? 'Process payments' : 'None pending'}
          tone="stone"
          icon="💳"
          to="/deals/payments"
        />
        <KPI
          title="Open Tickets"
          value={loading ? '-' : stats.openTickets}
          sub={stats.openTickets > 0 ? `${stats.openTickets} need attention` : 'All clear'}
          subAlert={stats.openTickets > 0}
          tone="sky"
          icon="📧"
          to="/tickets"
        />
      </section>

      {/* Due Today Section */}
      <section className="dueTodaySection">
        {dueTodayLoading ? (
          <div className="panel">
            <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Loading...
            </div>
          </div>
        ) : dueTodayItems.length === 0 ? (
          <div className="panel panel--clear">
            <div className="panel__head">
              <div>
                <div className="panel__title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="dueTodayIcon dueTodayIcon--clear">!</span> Due Today
                </div>
                <div className="panel__sub">Nothing due today - all clear!</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="panel panel--alert">
            <div className="panel__head">
              <div>
                <div className="panel__title" style={{ color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="dueTodayIcon dueTodayIcon--alert">!</span> Needs Dealing With Today
                </div>
                <div className="panel__sub">{dueTodayItems.length} pinned item{dueTodayItems.length !== 1 ? 's' : ''} due today</div>
              </div>
            </div>
            <div className="dueTodayList">
              {dueTodayItems.map(item => {
                const areaStyle = ISSUE_AREA_COLORS[item.issue_area] || ISSUE_AREA_COLORS.Other
                return (
                  <div
                    key={item.id}
                    className="dueTodayItem"
                    onClick={() => navigateToPinnedItem(item)}
                  >
                    <div className="dueTodayItem__header">
                      <span className="dueTodayItem__type">{item.entity_type}</span>
                      <span
                        className="dueTodayItem__area"
                        style={{ background: areaStyle.bg, color: areaStyle.color }}
                      >
                        {item.issue_area}
                      </span>
                    </div>
                    <div className="dueTodayItem__name">{item.entityName || `${item.entity_type} #${item.entity_id}`}</div>
                    <div className="dueTodayItem__reason">{item.reason}</div>
                    <button
                      className="dueTodayItem__btn"
                      onClick={(e) => markAsDealtWith(item.id, e)}
                    >
                      Mark Dealt With
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </section>

      {/* Lower Panels */}
      <section className="panelGrid">
        {/* Shortcuts Panel */}
        <div className="panel">
          <div className="panel__head">
            <div>
              <div className="panel__title">Shortcuts</div>
              <div className="panel__sub">Quick access to common tasks</div>
            </div>
            <Link to="/deals" className="panel__link">View All</Link>
          </div>

          <div className="taskList">
            <TaskRow
              label="Reprice Inventory"
              badge={null}
              icon="🏷️"
              to="/inventory/repricing"
            />
            <TaskRow
              label="Manage Channel Listings"
              badge={null}
              icon="📡"
              to="/inventory/channel-listings"
            />
            <TaskRow
              label="Book Collection"
              badge={stats.awaitingLabel > 0 ? stats.awaitingLabel : null}
              active={stats.awaitingLabel > 0}
              icon="📦"
              to="/deals/collections"
            />
            <TaskRow
              label="Receive Items"
              badge={null}
              icon="📥"
              to="/warehouse/receiving"
            />
            <TaskRow
              label="Deals Awaiting Payment"
              badge={stats.awaitingPayment > 0 ? stats.awaitingPayment : null}
              badgeAlert={stats.awaitingPayment > 0}
              icon="💳"
              to="/deals/payments"
            />
            <TaskRow
              label="Unfulfilled Orders"
              badge={stats.unfulfilled > 0 ? stats.unfulfilled : null}
              badgeAlert={stats.unfulfilled > 0}
              icon="🚚"
              to="/orders"
            />
          </div>
        </div>

        {/* Activity Feed Panel */}
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Activity Feed</div>
            <button className="iconBtn--ghost" type="button" aria-label="Refresh" onClick={loadActivities}>
              ↻
            </button>
          </div>

          <div className="feed">
            {activitiesLoading ? (
              <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                Loading...
              </div>
            ) : activities.length === 0 ? (
              <>
                <FeedItem
                  time="Just now"
                  icon="📊"
                  tone="mint"
                  title={`${totalPending} deals`}
                  body="pending action across all stages."
                />
                <FeedItem
                  time="Current"
                  icon="⚗️"
                  tone="sand"
                  title={`${stats.arrivedToTest} items`}
                  body="currently in testing queue."
                />
              </>
            ) : (
              activities.map(activity => (
                <FeedItem
                  key={activity.id}
                  time={formatActivityTime(activity.created_at)}
                  icon={activity.icon || '📌'}
                  tone={activity.type === 'collection_booked' ? 'mint' : activity.type === 'quote_chased' ? 'peach' : 'sand'}
                  title={activity.title}
                  body={activity.description || activity.user_name || ''}
                />
              ))
            )}
          </div>

          <Link to="/activities" className="btn--feed">View All Activities</Link>
        </div>
      </section>
    </div>
  )
}

export default Dashboard
